import { useState, useRef, useEffect, useCallback } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import styles from './calculatorScreen.module.css';

import prod1 from '../../images/חומרים 1.webp';
import prod2 from '../../images/חומרים 2.webp';
import prod3 from '../../images/חומרים 3.webp';

/* Count-up hook  -  uses a key to avoid StrictMode double-fire */
function useCountUp(target, key, duration = 1200) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const hasRun = useRef(null);

  useEffect(() => {
    if (target === null) { setDisplay(0); hasRun.current = null; return; }
    if (hasRun.current === key) return;
    hasRun.current = key;

    const start = performance.now();
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(target * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, key, duration]);

  return display;
}

function FloatingProduct({ src, className }) {
  return (
    <div className={className}>
      <img src={src} alt="" className={styles.prodImg} loading="lazy" />
    </div>
  );
}

const ChevronIcon = () => (
  <svg className={styles.selArr} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const treatments = [
  { name: 'עיצוב גבות', price: 70 },
  { name: 'הרמת ריסים', price: 250 },
  { name: 'הרמת גבות', price: 150 },
];

function CalculatorScreen() {
  const [ddOpen, setDdOpen] = useState(false);
  const [selected, setSelected] = useState(treatments[0]);
  const [countStr, setCountStr] = useState('');
  const [result, setResult] = useState(null);
  const [calcKey, setCalcKey] = useState(0);
  const inputRef = useRef(null);

  const animatedVal = useCountUp(result, calcKey);

  const selectTreat = (t) => {
    setSelected(t);
    setDdOpen(false);
    setResult(null);
  };

  const handleInput = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length > 3) return;
    setCountStr(val);
    setResult(null);
  };

  const calculate = useCallback(() => {
    const num = parseInt(countStr, 10);
    if (!num || num < 1) return;
    setResult(selected.price * num);
    setCalcKey((k) => k + 1);
  }, [countStr, selected]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') calculate();
  };

  const count = parseInt(countStr, 10) || 0;

  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <section className={styles.calc} id="calculator" ref={sectionRef}>
      <div className={styles.bg} />
      <div className={styles.glowTR} />
      <div className={styles.glowBL} />

      <FloatingProduct src={prod1} className={styles.floatProd1} />
      <FloatingProduct src={prod2} className={styles.floatProd2} />
      <FloatingProduct src={prod3} className={styles.floatProd3} />

      <div className={styles.container}>
        <div className={styles.sh} data-sr="0">
          <p className={styles.shPre}>לשאלה שבטוח עלתה לך לראש</p>
          <h2 className={styles.shH2}>
            כמה אפשר <em className={styles.em}>להרוויח</em> מהתחום?
          </h2>
          <p className={styles.shP}>בחרי את סוג הטיפול, תקלידי את מספר הטיפולים החודשי וחשבי את ההכנסה הפוטנציאלית שלך בחודש מהתחום</p>
        </div>

        <div className={styles.splitCard} data-sr="0.1">
            {/* ── Input Side (Cream) ── */}
            <div className={styles.inputSide}>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>סוג טיפול</label>
                <div className={styles.selW}>
                  <button
                    className={`${styles.selT} ${ddOpen ? styles.selTOpen : ''}`}
                    onClick={() => setDdOpen(!ddOpen)}
                  >
                    <span className={styles.selVal}>{selected.name}</span>
                    <ChevronIcon />
                  </button>

                  {ddOpen && (
                    <div className={styles.selDd}>
                      {treatments.map((t) => (
                        <div
                          key={t.name}
                          className={`${styles.selO} ${selected?.name === t.name ? styles.selOActive : ''}`}
                          onClick={() => selectTreat(t)}
                        >
                          <span className={styles.selOn}>{t.name}</span>
                          <span className={styles.selOp}>{t.price}₪</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>כמה טיפולים בחודש?</label>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  className={styles.typeInput}
                  value={countStr}
                  onChange={handleInput}
                  onKeyDown={handleKeyDown}
                  placeholder="הקלידי מספר..."
                />
              </div>

              <button
                className={`${styles.calcBtn} ${countStr && count > 0 ? styles.calcBtnActive : ''}`}
                onClick={calculate}
                disabled={!countStr || count < 1}
              >
                <span className={styles.calcBtnText}>חשבי את ההכנסה שלי</span>
              </button>
            </div>

            {/* ── Result Side (Dark Brown) ── */}
            <div className={`${styles.resultSide} ${result !== null ? styles.resultSideVisible : ''}`}>
              <div className={styles.resultGlow} />

              {result !== null ? (
                <div
                  key={calcKey}
                  className={`${styles.resultContent} ${styles.resultEnter}`}
                >
                  <p className={styles.resultLabel}>הכנסה חודשית משוערת</p>
                  <div className={styles.resultAmWrap}>
                    <span className={styles.resultCur}>₪</span>
                    <span className={styles.resultAm}>{animatedVal.toLocaleString('he-IL')}</span>
                  </div>
                  <div className={styles.resultDivider} />
                  <p className={styles.resultNote}>
                    זה הפוטנציאל שלך - <em>עם הידע הנכון</em>
                  </p>
                </div>
              ) : (
                <div className={`${styles.resultPlaceholder} ${styles.placeholderEnter}`}>
                  <span className={styles.placeholderIcon}>₪</span>
                  <p className={styles.placeholderText}>הזיני את הפרטים ולחצי על חשבי</p>
                </div>
              )}
            </div>
          </div>
      </div>
    </section>
  );
}

export default CalculatorScreen;
